import React, { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api, { API, pollJob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Empty, Spinner } from "@/components/common";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, CartesianGrid,
} from "recharts";
import {
  AlertTriangle, Gauge, FileText, Download, Loader2, Sparkles, Languages, Filter, CheckCircle2,
  MessageSquareWarning, ListChecks, HelpCircle, TimerReset, Quote, Clock,
} from "lucide-react";

const NAVY = "#0f2557", CYAN = "#29abe2", GOLD = "#c9a227";
const fmt = (s) => `${String(Math.floor((s || 0) / 60)).padStart(2, "0")}:${String(Math.floor((s || 0) % 60)).padStart(2, "0")}`;
const sev = { Critical: "bg-red-500/15 text-red-500", High: "bg-red-500/15 text-red-500", Medium: "bg-[#c9a227]/15 text-[#a6821a]", Low: "bg-[#29abe2]/15 text-[#1a7bb0]" };
const stat = { Answered: "bg-emerald-500/15 text-emerald-600", "Partially answered": "bg-[#c9a227]/15 text-[#a6821a]", Open: "bg-[#c9a227]/15 text-[#a6821a]", Covered: "bg-emerald-500/15 text-emerald-600", "Covered": "bg-emerald-500/15 text-emerald-600" };

const Tile = ({ label, value, sub, testid }) => (
  <div className="flex-1 min-w-[120px] rounded-xl border bg-card border-l-4 border-l-[#29abe2] p-3" data-testid={testid}>
    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
    <div className="text-2xl font-bold text-[#0f2557] dark:text-[#29abe2]">{value}</div>
    <div className="text-[11px] text-muted-foreground">{sub}</div>
  </div>
);

function HealthScore({ health }) {
  if (!health) return null;
  const score = health.score;
  const color = score == null ? "#d9544e" : score >= 75 ? "#2e9e5b" : score >= 60 ? GOLD : "#d9544e";
  return (
    <div className="rounded-xl border bg-card p-5" data-testid="health-score">
      <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><Gauge size={18} /> Meeting Health Score</div>
      <div className="flex gap-5 items-start flex-wrap">
        <div className="w-24 h-24 rounded-full grid place-items-center shrink-0" style={{ border: `7px solid ${color}` }}>
          <div className="text-3xl font-extrabold leading-none" style={{ color }}>{score == null ? "—" : score}</div>
          <div className="text-[10px] text-muted-foreground">{score == null ? "" : "/ 100"}</div>
        </div>
        <div className="flex-1 min-w-[260px] space-y-2">
          <div className="text-sm font-medium" style={{ color }}>{health.status}</div>
          {health.components?.map((c) => {
            const cc = c.score >= 75 ? "#2e9e5b" : c.score >= 50 ? GOLD : "#d9544e";
            return (
              <div key={c.key}>
                <div className="flex justify-between text-[11px]"><span>{c.label} <span className="text-muted-foreground">({c.weight}%)</span></span><b>{Math.round(c.score)}</b></div>
                <div className="h-1.5 bg-secondary rounded"><div className="h-full rounded" style={{ width: `${Math.min(100, c.score)}%`, background: health.suppressed ? "#cbd5e1" : cc }} /></div>
                <div className="text-[10px] text-muted-foreground">{c.tip}</div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function MeetingInsights({ meeting, mid, transcripts, refresh }) {
  const [busy, setBusy] = useState("");
  const [mom, setMom] = useState(null);
  const [view, setView] = useState("english");
  const [translit, setTranslit] = useState(false);
  const [hideExcluded, setHideExcluded] = useState(false);

  const a = meeting?.analysis;
  const active = (transcripts || []).find((t) => t.active) || (transcripts || [])[0];
  const qr = active?.quality_report;
  const gateLow = qr?.gate?.status === "low";

  const anQ = useQuery({
    queryKey: ["analytics", mid, meeting?.status],
    queryFn: async () => (await api.get(`/meetings/${mid}/analytics`)).data,
    enabled: !!a,
  });
  const an = anQ.data;

  const genMom = async () => {
    setBusy("mom");
    try {
      const r = await api.post(`/meetings/${mid}/mom/generate`);
      const res = await pollJob(r.data.job_id);
      setMom(res);
      toast.success(res.quality_gate === "low" ? "MoM generated (DRAFT — quality low)" : "Minutes of Meeting generated");
    } catch (e) { toast.error(e?.response?.data?.detail || e.message || "MoM generation failed"); }
    finally { setBusy(""); }
  };
  const retranscribe = async () => { toast.message("Re-transcribe", { description: "Go to the Record/Review step to upload audio again with a forced language." }); };

  if (!a) return <Empty title="Analyze the meeting first" hint="Run analysis on a validated transcript to unlock analytics and minutes." />;
  if (anQ.isLoading) return <Spinner label="Loading analytics…" />;

  const k = an?.kpis || {};
  const speaking = an?.charts?.speaking_time || [];
  const qd = an?.charts?.question_status || { answered: 0, open: 0 };
  const byOwner = an?.charts?.actions_by_owner || [];
  const donutData = [{ name: "Answered", value: qd.answered }, { name: "Open", value: qd.open }];

  return (
    <div className="space-y-5">
      {gateLow && (
        <div className="rounded-xl border border-red-400 bg-red-500/10 p-4 flex items-start gap-3" data-testid="quality-low-banner">
          <AlertTriangle className="text-red-500 mt-0.5" size={18} />
          <div className="flex-1">
            <div className="font-semibold text-red-600">Transcript quality low</div>
            <div className="text-sm text-red-600/80">{(qr?.gate?.reasons || []).join(" · ")}. Health Score and drift are shown as “Insufficient data”. Minutes export as DRAFT.</div>
          </div>
          <Button size="sm" variant="outline" onClick={retranscribe} className="gap-1.5"><TimerReset size={14} /> Re-transcribe</Button>
        </div>
      )}

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="transcript" data-testid="tab-transcript">Transcript</TabsTrigger>
          <TabsTrigger value="minutes" data-testid="tab-minutes">AI Minutes</TabsTrigger>
          <TabsTrigger value="tasks" data-testid="tab-tasks">Tasks</TabsTrigger>
          <TabsTrigger value="decisions" data-testid="tab-decisions">Decisions</TabsTrigger>
          <TabsTrigger value="questions" data-testid="tab-questions">Questions</TabsTrigger>
          <TabsTrigger value="issues" data-testid="tab-issues">Issues</TabsTrigger>
          <TabsTrigger value="analytics" data-testid="tab-analytics">Analytics</TabsTrigger>
        </TabsList>

        {/* OVERVIEW */}
        <TabsContent value="overview" className="mt-5 space-y-5">
          <div className="flex flex-wrap gap-2.5">
            <Tile label="Duration" value={k.duration_label || "—"} sub={`${k.wpm ?? "—"} words/min`} testid="kpi-duration" />
            <Tile label="On agenda" value={k.on_agenda_pct == null ? "—" : `${k.on_agenda_pct}%`} sub={gateLow ? "Insufficient data" : `${k.drift_count ?? 0} drift`} testid="kpi-agenda" />
            <Tile label="Speakers" value={k.speakers ?? "—"} sub={`Lead ${k.lead_speaker || "—"}`} testid="kpi-speakers" />
            <Tile label="Decisions" value={k.decisions ?? 0} sub="recorded" testid="kpi-decisions" />
            <Tile label="Actions" value={k.action_items ?? 0} sub="with owners" testid="kpi-actions" />
            <Tile label="Questions" value={k.questions ?? 0} sub={`${k.questions_unanswered ?? 0} open`} testid="kpi-questions" />
            <Tile label="Issues" value={k.issues ?? 0} sub="reported" testid="kpi-issues" />
            <Tile label="Transcript" value={`${qr?.coverage_pct ?? "—"}%`} sub="coverage" testid="kpi-quality" />
          </div>

          <HealthScore health={an?.health} />

          <div className="grid lg:grid-cols-2 gap-4">
            <div className="rounded-xl border bg-card p-5">
              <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Speaking time</div>
              <ResponsiveContainer width="100%" height={Math.max(120, speaking.length * 42)}>
                <BarChart data={speaking} layout="vertical" margin={{ left: 10, right: 20 }}>
                  <XAxis type="number" hide /><YAxis type="category" dataKey="speaker" width={90} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => `${v}%`} />
                  <Bar dataKey="share" radius={[0, 4, 4, 0]} fill={CYAN} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="rounded-xl border bg-card p-5">
              <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Question status</div>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={donutData} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={2}>
                    <Cell fill={NAVY} /><Cell fill="#e1e8f0" />
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="text-center text-sm text-muted-foreground">{qd.answered} answered · {qd.open} open</div>
            </div>
          </div>

          <div className="rounded-xl border bg-card p-5">
            <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Executive summary</div>
            <p className="text-sm text-foreground whitespace-pre-line">{a.executive_summary}</p>
            {a.participation_note && <p className="text-xs text-muted-foreground mt-2">{a.participation_note}</p>}
          </div>

          {(a.topics || []).length > 0 && (
            <div className="rounded-xl border bg-card p-5">
              <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3">Topics discussed</div>
              <div className="space-y-3">{a.topics.map((t) => (
                <div key={t.id} className="border-l-2 border-[#29abe2]/40 pl-3">
                  <div className="text-sm font-medium text-foreground">{t.title} <span className="text-[10px] text-muted-foreground font-mono">{t.start}–{t.end}</span></div>
                  <div className="text-xs text-muted-foreground">{t.summary}</div>
                  {t.outcome && <div className="text-xs text-[#1a7bb0] mt-0.5">Outcome: {t.outcome}</div>}
                </div>
              ))}</div>
            </div>
          )}

          {(a.key_quotes || []).length > 0 && (
            <div className="rounded-xl border bg-card p-5">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3"><Quote size={16} /> Key quotes</div>
              <div className="space-y-3">{a.key_quotes.map((q, i) => (
                <blockquote key={i} className="border-l-2 border-[#29abe2] pl-3">
                  {q.original && q.original !== q.english && <div className="text-xs italic text-muted-foreground">{q.original}</div>}
                  <div className="text-sm text-foreground">{q.english}</div>
                  <div className="text-[10px] text-muted-foreground">— {q.speaker} · {q.ts}{q.why ? ` · ${q.why}` : ""}</div>
                </blockquote>
              ))}</div>
            </div>
          )}

          {!gateLow && (an?.drift_incidents || []).length > 0 && (
            <div className="rounded-xl border border-[#c9a227]/40 bg-[#c9a227]/5 p-5">
              <div className="flex items-center gap-2 font-semibold text-[#a6821a] mb-2"><AlertTriangle size={16} /> Agenda drift</div>
              {an.drift_incidents.map((d) => (
                <div key={d.id} className="text-sm py-0.5">{d.start}–{d.end} ({d.duration_min}m) → <b>{d.topic}</b>, initiated by {d.initiated_by}. {d.returned ? "Returned to agenda." : "Did not return."}</div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TRANSCRIPT */}
        <TabsContent value="transcript" className="mt-5 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex rounded-lg border overflow-hidden text-xs">
              {["original", "english", "side"].map((v) => (
                <button key={v} onClick={() => setView(v)} className={`px-3 py-1.5 ${view === v ? "bg-[#0f2557] text-white" : "hover:bg-secondary"}`} data-testid={`view-${v}`}>{v === "side" ? "Side-by-side" : v[0].toUpperCase() + v.slice(1)}</button>
              ))}
            </div>
            <Button size="sm" variant={translit ? "default" : "outline"} className="h-8 gap-1.5" onClick={() => setTranslit(!translit)} data-testid="translit-toggle"><Languages size={13} /> Transliteration</Button>
            <Button size="sm" variant={hideExcluded ? "default" : "outline"} className="h-8 gap-1.5" onClick={() => setHideExcluded(!hideExcluded)} data-testid="excluded-filter"><Filter size={13} /> {hideExcluded ? "Show excluded" : "Hide excluded"}</Button>
            <span className="text-xs text-muted-foreground ml-auto">Languages: {(qr?.languages || []).map((l) => `${l.lang} ${l.pct}%`).join(" · ") || "—"}</span>
          </div>
          <div className="rounded-xl border bg-card p-4 max-h-[560px] overflow-y-auto" data-testid="insights-transcript">
            {(active?.segments || []).filter((s) => !(hideExcluded && s.quality_verdict === "excluded")).map((s, i) => {
              const excluded = s.quality_verdict === "excluded";
              const flagged = s.quality_verdict === "flagged";
              const repaired = s.quality_verdict === "repaired";
              return (
                <div key={i} className={`flex gap-3 py-2 border-b border-border/40 last:border-0 ${excluded ? "opacity-50 bg-red-500/5" : flagged ? "bg-[#c9a227]/5" : ""}`} data-testid={`seg-${i}`}>
                  <div className="w-24 shrink-0">
                    <div className="text-xs font-semibold text-[#0f2557] dark:text-[#29abe2]">{s.speaker}</div>
                    <div className="text-[10px] text-muted-foreground font-mono">{fmt(s.start)}</div>
                    <div className="mt-0.5 flex flex-wrap gap-1">
                      <span className="text-[9px] px-1 rounded bg-secondary">{s.lang || "?"}</span>
                      {excluded && <span className="text-[9px] px-1 rounded bg-red-500/15 text-red-500">excluded</span>}
                      {flagged && <span className="text-[9px] px-1 rounded bg-[#c9a227]/15 text-[#a6821a]">flagged</span>}
                      {repaired && <span className="text-[9px] px-1 rounded bg-emerald-500/15 text-emerald-600">repaired</span>}
                    </div>
                  </div>
                  <div className="flex-1 text-sm">
                    {view === "side" ? (
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="text-muted-foreground italic">{s.original_text || s.text}</div>
                        <div className="text-foreground">{s.translation_en || s.text}</div>
                      </div>
                    ) : view === "original" ? (
                      <div className="text-foreground">{s.original_text || s.text}</div>
                    ) : (
                      <div className="text-foreground">{s.translation_en || s.text}</div>
                    )}
                    {translit && s.transliteration && <div className="text-[11px] text-muted-foreground mt-0.5">{s.transliteration}</div>}
                    {(s.flags || []).length > 0 && <div className="text-[10px] text-red-500/70 mt-0.5">⚠ {s.flags.join(", ")}</div>}
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* AI MINUTES */}
        <TabsContent value="minutes" className="mt-5 space-y-4">
          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2]"><FileText size={18} /> Comprehensive Minutes of Meeting</div>
              <Button onClick={genMom} disabled={busy === "mom"} className="gap-2" data-testid="generate-mom">
                {busy === "mom" ? <Loader2 className="animate-spin" size={15} /> : <Sparkles size={15} />} {mom ? "Regenerate MoM" : "Generate MoM"}
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-2">Board-ready 18-section minutes with attendance, agenda coverage, decisions, actions, questions, issues, risks, confirmation points and a transcript-quality report. Original-language quotes render alongside English.</p>
            {mom && (
              <div className="mt-4 space-y-3">
                {mom.quality_gate === "low" && <div className="text-sm text-red-500 flex items-center gap-1.5"><AlertTriangle size={14} /> Exported as DRAFT — transcript quality low.</div>}
                {mom.consistency && !mom.consistency.ok && (
                  <div className="text-xs rounded-lg bg-[#c9a227]/10 p-3">
                    <div className="font-semibold text-[#a6821a] mb-1">Consistency review flagged:</div>
                    <ul className="list-disc pl-4 text-[#a6821a]/90">{mom.consistency.issues.map((x, i) => <li key={i}>{x}</li>)}</ul>
                  </div>
                )}
                {mom.consistency?.ok && <div className="text-xs text-emerald-600 flex items-center gap-1.5"><CheckCircle2 size={13} /> Consistency review passed.</div>}
                <div className="flex gap-2 flex-wrap">
                  <a href={`${API}/documents/${mom.document_id}/export?format=pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5" data-testid="export-mom-pdf"><Download size={13} /> PDF</Button></a>
                  <a href={`${API}/documents/${mom.document_id}/export?format=docx`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5"><Download size={13} /> DOCX</Button></a>
                  <a href={`${API}/documents/${mom.document_id}/export?format=summary-pdf`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5"><Download size={13} /> Summary PDF</Button></a>
                  <a href={`${API}/documents/${mom.document_id}/export?format=actions-xlsx`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline" className="h-8 gap-1.5"><Download size={13} /> Actions XLSX</Button></a>
                  <a href={`${API}/documents/${mom.document_id}/export?format=html`} target="_blank" rel="noreferrer"><Button size="sm" variant="ghost" className="h-8 gap-1.5">Preview</Button></a>
                </div>
              </div>
            )}
          </div>
          {(a.confirmation_points || meeting.confirmation_points || []).length > 0 && (
            <div className="rounded-xl border-l-4 border-[#c9a227] bg-card p-5">
              <div className="font-semibold text-[#a6821a] mb-2">Points requiring confirmation</div>
              {(meeting.confirmation_points || []).map((c, i) => (
                <div key={i} className="text-xs py-0.5">{c.start}–{c.end} · {c.speaker} · <span className="text-red-500">{c.reason}</span> — “{c.snippet}”</div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* TASKS */}
        <TabsContent value="tasks" className="mt-5">
          <Table icon={<ListChecks size={16} />} title="Action items & tasks"
            cols={["ID", "Action", "Category", "Owner", "Due", "Priority", "Time"]}
            rows={(a.action_items || []).map((x) => [x.id, x.description, x.category, x.owner || "—", x.due || "—", <span className={`text-[10px] px-1.5 py-0.5 rounded ${sev[x.priority] || "bg-secondary"}`}>{x.priority}</span>, x.ts])} />
        </TabsContent>
        <TabsContent value="decisions" className="mt-5">
          <Table icon={<CheckCircle2 size={16} />} title="Decisions log"
            cols={["ID", "Decision", "Decided by", "Impact", "Time"]}
            rows={(a.decisions || []).map((x) => [x.id, x.description, x.decided_by, x.impact, x.ts])} />
        </TabsContent>
        <TabsContent value="questions" className="mt-5">
          <Table icon={<HelpCircle size={16} />} title="Questions raised"
            cols={["ID", "Question", "Asked by", "Status", "Owner", "Time"]}
            rows={(a.questions || []).map((x) => [x.id, x.question, x.asked_by, <span className={`text-[10px] px-1.5 py-0.5 rounded ${stat[x.status] || "bg-secondary"}`}>{x.status}</span>, x.owner, x.ts])} />
        </TabsContent>
        <TabsContent value="issues" className="mt-5">
          <Table icon={<MessageSquareWarning size={16} />} title="Issues & problems reported"
            cols={["ID", "Issue", "Module", "Reported by", "Severity", "Status", "Time"]}
            rows={(a.issues || []).map((x) => [x.id, x.description, x.module, x.reported_by, <span className={`text-[10px] px-1.5 py-0.5 rounded ${sev[x.severity] || "bg-secondary"}`}>{x.severity}</span>, x.status, x.ts])} />
        </TabsContent>

        {/* ANALYTICS */}
        <TabsContent value="analytics" className="mt-5 space-y-4">
          <div className="rounded-xl border bg-card p-5">
            <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Action items by owner</div>
            <ResponsiveContainer width="100%" height={Math.max(120, byOwner.length * 40)}>
              <BarChart data={byOwner} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid horizontal={false} stroke="#eef2f8" />
                <XAxis type="number" allowDecimals={false} hide /><YAxis type="category" dataKey="owner" width={100} tick={{ fontSize: 11 }} />
                <Tooltip /><Bar dataKey="count" radius={[0, 4, 4, 0]} fill={NAVY} />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2"><Clock size={16} /> Meeting timeline</div>
            <div className="space-y-1.5">{(a.topics || []).map((t) => (
              <div key={t.id} className="flex items-center gap-3 text-sm"><span className="font-mono text-[10px] text-muted-foreground w-24">{t.start}–{t.end}</span><span className="text-foreground">{t.title}</span></div>
            ))}</div>
          </div>
          <div className="rounded-xl border bg-card p-5">
            <div className="font-semibold text-[#0f2557] dark:text-[#29abe2] mb-2">Transcript quality report</div>
            <div className="grid sm:grid-cols-4 gap-3 text-sm">
              <div><div className="text-muted-foreground text-xs">Coverage</div><div className="font-semibold">{qr?.coverage_pct ?? "—"}%</div></div>
              <div><div className="text-muted-foreground text-xs">Avg confidence</div><div className="font-semibold">{qr?.avg_confidence ?? "—"}</div></div>
              <div><div className="text-muted-foreground text-xs">Words/min</div><div className="font-semibold">{qr?.wpm ?? "—"}</div></div>
              <div><div className="text-muted-foreground text-xs">Excluded</div><div className="font-semibold">{qr?.segments_excluded ?? 0} ({qr?.excluded_pct ?? 0}%)</div></div>
            </div>
            <GapMap gap={qr?.gap_map} duration={qr?.duration_sec} />
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function GapMap({ gap, duration }) {
  if (!gap?.length || !duration) return null;
  const color = { transcribed: CYAN, repaired: GOLD, flagged: GOLD, excluded: "#d9544e" };
  return (
    <div className="mt-3">
      <div className="flex h-4 rounded overflow-hidden" data-testid="gap-map">
        {gap.map((g, i) => <div key={i} style={{ width: `${Math.max(0.3, (100 * (g.end - g.start)) / duration)}%`, background: color[g.kind] || "#e1e8f0" }} title={`${fmt(g.start)}–${fmt(g.end)} ${g.kind}`} />)}
      </div>
      <div className="flex gap-3 text-[10px] text-muted-foreground mt-1">
        <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: CYAN }} />Transcribed</span>
        <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: GOLD }} />Repaired</span>
        <span className="flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ background: "#d9544e" }} />Excluded</span>
      </div>
    </div>
  );
}

function Table({ icon, title, cols, rows }) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center gap-2 font-semibold text-[#0f2557] dark:text-[#29abe2] mb-3">{icon} {title} <span className="text-sm text-muted-foreground font-normal">({rows.length})</span></div>
      {rows.length === 0 ? <p className="text-sm text-muted-foreground">None captured.</p> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-muted-foreground border-b">{cols.map((c) => <th key={c} className="py-1.5 pr-3 font-medium">{c}</th>)}</tr></thead>
          <tbody>{rows.map((r, i) => (
            <tr key={i} className="border-b border-border/40 align-top">{r.map((c, j) => <td key={j} className="py-1.5 pr-3 text-foreground">{c}</td>)}</tr>
          ))}</tbody>
        </table></div>
      )}
    </div>
  );
}
